import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import { useSelector } from 'react-redux';
import { getNavbarHeight, getViewportDimensions } from '../../../../models/Utils';
import { credoStoryTypes } from '../../../../models/Models';

import { ADALandmarks, ADAARIARoles } from '../../../../models/ADA';

import LearnMoreLink from '../../../Common/LearnMoreLink/LearnMoreLink';

import './StoryDisplay.scss';

const StoryDisplay = React.forwardRef(({ story, general, onClose }, theRef) => {
  const menuOpened = useSelector(state => state.data.menuOpened);
  const mediaPath = useSelector(state => state.config.basePath + state.config.apiMediaPath);
  const [videoShown, setVideoShown] = useState(false);
  const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 });
  const imgHolderRef = useRef(null);
  const headerRef = useRef(null);
  const [showingSchemeOverlay, setShowingSchemeOverlay] = useState(false);

  useEffect(() => {
    if (showingSchemeOverlay && headerRef.current) {
      setTimeout(() => {
        headerRef.current.getElementsByClassName('credo-ss-sd-scheme')[0].focus();
      }, 10);
    }
  }, [showingSchemeOverlay]);

  const getImageColumn = () => {
    const img = story.Template === credoStoryTypes.VideoText || story.Template === credoStoryTypes.VideoTextCompany ? story.Video.BackgroundMedia : story.Photo.Image;
    const alt = story.Template === credoStoryTypes.VideoText || story.Template === credoStoryTypes.VideoTextCompany ? story.Video.AltText : story.Photo.AltText;
    const imgHolderClass = story.Template === credoStoryTypes.VideoText || story.Template === credoStoryTypes.VideoTextCompany ? 'video-image' : '';
    return (
      <div className="credo-ss-sd-image-column">
        <div className="credo-ss-sd-image-column-inner">
          <div className={`credo-ss-sd-image-holder ${imgHolderClass} ${videoShown ? 'showing-video' : ''}`} ref={imgHolderRef}>
            <img className="credo-ss-sd-image" src={`${mediaPath}${img}`} alt={alt} />
            {
              (story.Template === credoStoryTypes.VideoText || story.Template === credoStoryTypes.VideoTextCompany) && !videoShown
              &&
              <button
                className="credo-ss-sd-play-button"
                tabIndex={!menuOpened ? 0 : -1}
                onClick={() => {
                  const { width, height } = imgHolderRef.current.getBoundingClientRect();
                  setVideoDimensions({ width, height });
                  setVideoShown(true);
                }}
              ><span>Play Video</span></button>
            }
            {
              (story.Template === credoStoryTypes.VideoText || story.Template === credoStoryTypes.VideoTextCompany) && videoShown
              &&
              <div className="credo-ss-sd-video-holder">
                <ReactPlayer
                  url={story.Video.URL}
                  width={videoDimensions.width}
                  height={videoDimensions.height}
                  muted={true}
                  loop={false}
                  playing={true}
                  controls={true}
                  playsinline={true}
                  volume={1}
                  config={{
                    youtube: story.Video.URL.indexOf("youtube.com") < 0 ? null : {
                      playerVars: {
                        autoplay: 1,
                        showinfo: 1,
                        playsinline: 1,
                        modestbranding: 1
                      },
                    }
                  }}
                />
              </div>
            }
          </div>
        </div>
      </div>
    );
  };

  const getContentColumn = () => {
    const textTitle = 
      (story.Template === credoStoryTypes.PhotoText || story.Template === credoStoryTypes.PhotoTextCompany || story.Template === credoStoryTypes.VideoText || story.Template === credoStoryTypes.VideoTextCompany)
      ?
      <div className="credo-ss-sd-text-title">{story.TextTitle}</div>
      :
      null;

    const textBody = 
      (story.Template === credoStoryTypes.PhotoText || story.Template === credoStoryTypes.PhotoTextCompany || story.Template === credoStoryTypes.VideoText || story.Template === credoStoryTypes.VideoTextCompany)
      ?
      <div className="credo-ss-sd-text-body">{story.TextBody}</div>
      :
      null;

    const textQuote =
      (story.Template === credoStoryTypes.PhotoQuote || story.Template === credoStoryTypes.PhotoQuoteCompany)
      ?
      <div className="credo-ss-sd-text-quote">“{story.Quote}”</div>
      :
      null;

    const companyPart =
      (story.Template === credoStoryTypes.PhotoTextCompany || story.Template === credoStoryTypes.PhotoQuoteCompany || story.Template === credoStoryTypes.VideoTextCompany)
      ?
      <div className="credo-ss-sd-company">
        <div className="credo-ss-sd-company-image-holder">
          <img className="credo-ss-sd-company-image" src={`${mediaPath}${story.CompanyInfo.Logo.Image}`} alt={story.CompanyInfo.Logo.AltText} />
        </div>
        <div className="credo-ss-sd-company-text-and-button">
          <div className="credo-ss-sd-company-title">{story.CompanyInfo.Title}</div>
          <button
            className="credo-button"
            tabIndex={!menuOpened ? 0 : -1}
            onClick={() => setShowingSchemeOverlay(true)}
            aria-label={story.CompanyInfo.LearnMoreAltText ? story.CompanyInfo.LearnMoreAltText : general.CompanyLearnMoreButtonAltText}
          >{general.CompanyLearnMore}</button>
        </div>
      </div>
      :
      null;

    return (
      <div className="credo-ss-sd-content-column">
        <div className="credo-ss-sd-content-sizer"></div>
        <div className="credo-ss-sd-content">
          {textTitle}
          {textBody}
          {textQuote}
          {companyPart}
        </div>
      </div>
    );
  };

  const handleX = () => {
    if (showingSchemeOverlay) {
      setShowingSchemeOverlay(false);
    } else {
      onClose();
    }
  };

  useLayoutEffect(() => {
    setVideoShown(false);
    //headerRef.current.focus();
  }, [story]);

  return (
    <div
      className="credo-ss-story-display" 
      ref={headerRef}
      tabIndex="-1"
      role={ADAARIARoles.Article}
      aria-label={`${story.Name}, ${story.JobTitle}`}
      aria-haspopup={"dialog"}
    >
      <div className="credo-ss-sd-wrapper">
        <div className="credo-ss-sd-holder">
          <div className="cpvi-grid-container">
            <button className="credo-ss-sd-big-close-button" onClick={handleX} tabIndex="-1" aria-hidden={true} />
            <div className="cpvi-grid-row">
              <div className="cpvi-grid-col w12">
                <div className="credo-ss-sd-header">
                  <div className="credo-ss-sd-header-text">
                    <span className="credo-ss-sd-name">{story.Name}</span><br className="credo-ss-sd-header-break"/><span className="credo-ss-sd-job-title">{story.JobTitle}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="cpvi-grid-row credo-ss-sd-body">
              <div className="cpvi-grid-col w6 credo-ss-sd-body-column">
                {getImageColumn()}
              </div>
              {
                !showingSchemeOverlay
                &&
                <div className="cpvi-grid-col w6 credo-ss-sd-body-column">
                  {getContentColumn()}
                </div>
              }
              {
                (story.Template === credoStoryTypes.PhotoTextCompany || story.Template === credoStoryTypes.PhotoQuoteCompany || story.Template === credoStoryTypes.VideoTextCompany)
                &&
                showingSchemeOverlay
                &&
                <div className="credo-ss-sd-scheme-overlay">
                  <div className="cpvi-grid-col w12 credo-ss-sd-scheme-holder">
                    <div
                      className="credo-ss-sd-scheme"
                      style={{ backgroundImage: `url("${mediaPath}${story.CompanyInfo.LearnMoreImage.Image}")` }}
                      role={ADAARIARoles.Image}
                      aria-label={story.CompanyInfo.LearnMoreImage.AltText}
                    ></div>
                    <div className="credo-ss-sd-scheme-caption">{story.CompanyInfo.LearnMoreImageCaption}</div>
                  </div>
                </div>
              }
            </div>
            <button className="credo-ss-sd-close desktop" onClick={handleX}><span>{general.StoryCloseAltText}</span></button>
          </div>
        </div>
      </div>
      <button className="credo-ss-sd-close mobile" onClick={handleX}><span>{general.StoryCloseAltText}</span></button>
    </div>
  );
});

export default StoryDisplay;